import { Injectable, inject } from '@angular/core';
import {
  Observable,
  take,
  catchError,
  BehaviorSubject,
  filter,
  switchMap,
  of,
  map,
  tap,
  throwError,
} from 'rxjs';
import { ApiServiceBase } from './ApiServiceBase';
import { Cart } from '../common/Cart';
import { CartPosition } from '../common/CartPosition';
import { AppProfileService } from './profile.service';
import { OrderGroup } from '../common/OrderGroup';
import { CatalogSku } from '../common/CatalogSku';
import { withNormalisedReservationStatus } from '../common/ReservationStatus';

@Injectable({
  providedIn: 'root',
})
export class CartService extends ApiServiceBase {
  private profileService = inject(AppProfileService);

  public $cart: BehaviorSubject<Cart> = new BehaviorSubject<Cart>(null);
  public cart: Cart;

  protected readonly path = 'cart';

  getByCustomerId(id: string): Observable<Cart> {
    return this.http
      .get<Cart>(this.getUrl() + '/forcustomer/' + id)
      .pipe(take(1));
  }

  addToCart(
    catalogSku: CatalogSku,
    count: number = 1
  ): Observable<boolean> {
    const body = {
      skuId: catalogSku.skuId,
      delta: count,
    };
    return this.http
      .post<Cart>(this.getUrl() + '/items', body)
      .pipe(
        switchMap((cart) => {
          this.cart = cart;
          this.$cart.next(cart);
          return of(true);
        }),
        // #456: propagate the error (was swallowed with of(false), which wrongly showed a success
        // toast). A 409 here means a checkout is in flight for this cart — the caller surfaces it.
        catchError((err) => {
          console.error('Add to cart failed', err);
          return throwError(() => err);
        })
      );
  }

  editCartItem(cartPosition: CartPosition, count: number): Observable<boolean> {
    const url = `${this.getUrl()}/items/${cartPosition.id}`;
    const request$ =
      count === 0
        ? this.http.delete<Cart>(url)
        : this.http.patch<Cart>(url, { count });

    return request$.pipe(
      switchMap((cart) => {
        this.cart = cart;
        this.$cart.next(cart);
        return of(true);
      }),
      // #456: propagate the error (was swallowed with of(false)). A 409 means a checkout is in flight.
      catchError((err) => {
        console.error('Edit cart item failed', err);
        return throwError(() => err);
      })
    );
  }

  public reloadCart(): void {
    this.getCartFromServer().subscribe({
      next: (cart) => {
        this.cart = cart;
        this.$cart.next(cart);
      },
    });
  }

  private getCartFromServer(): Observable<Cart> {
    return this.profileService.currentProfile$.pipe(
      // There is no cart without a customer to fetch it for. `currentProfile$`
      // emits null by design for a signed-out visitor (profile.service.ts
      // getCurrent() returns of(null)), and a signed-in user whose profile is
      // not yet approved has no `customer` — both used to throw a TypeError
      // here, which reloadCart() has no error handler for. Skipping instead
      // leaves the cart untouched, which is what those callers already assumed.
      filter((p) => !!p?.customer),
      switchMap((p) => this.getByCustomerId(p.customer.id))
    );
  }

  checkout(): Observable<OrderGroup> {
    return this.http
      .post<OrderGroup>(this.getUrl() + '/checkout', null)
      .pipe(
        // #168: the API sends reservationStatus as a camelCase string. Normalising here — the
        // one place a checkout result enters the app — is what makes the caller's branching
        // work at all.
        map(withNormalisedReservationStatus),
        tap(()=>{
        this.reloadCart();
      }));
  }
}
